"use client";
import Login from "@/src/components/login/login";
import { Button } from "@/src/components/ui/button";
import Loading from "@/src/components/ui/loading";
import { LanguageSwitcher } from "@/src/components/ui/language-switcher";
import { useToast } from "@/src/hooks/use-toast";
import { Moon, Sun } from "lucide-react";
import { useSession } from "next-auth/react";
import { useTheme } from "next-themes";
import { useRouter } from "@/src/i18n/routing";
import { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import { saveToLocalStorage } from "@/src/components/dashboard/shared/helpers";
import { DashboardStateV2 } from "@/src/components/dashboard/shared/types";
import { fetchDashboardLayoutAction } from "./action";

interface LoginPageClientProps {
  /** Layout prefetchado por el Server Component (null si el usuario no tenía sesión al renderizar). */
  prefetchedLayout: DashboardStateV2 | null;
}

export default function LoginPageClient({
  prefetchedLayout,
}: LoginPageClientProps) {
  const t = useTranslations("Login");
  const tema = useTranslations();
  const { toast } = useToast();
  const { theme, setTheme } = useTheme();
  const [timeToRedirect, setTimeToRedirect] = useState(5);
  const { data: session, status } = useSession();
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  const handleThemeChange = () => {
    toast({
      title: tema("Theme.title"),
      description:
        tema("Theme.changed") +
        (theme === "dark"
          ? ` ${tema("Theme.toLight")}`
          : ` ${tema("Theme.toDark")}`),
    });
    setTheme(theme === "dark" ? "light" : "dark");
  };

  // Persiste el layout en localStorage en cuanto el usuario está autenticado.
  // Si el Server Component ya prefetchó (usuario tenía sesión), lo usa directamente.
  // Si es un login fresco, llama el Server Action para obtenerlo.
  useEffect(() => {
    if (status !== "authenticated") return;

    async function persistLayout() {
      try {
        const layout = prefetchedLayout ?? (await fetchDashboardLayoutAction());
        if (layout) {
          saveToLocalStorage(layout);
          setTheme(layout.theme);
        } else {
          localStorage.removeItem("dashboardLayout"); // forzar fetch fresco en el dashboard
        }
      } catch {
        // Fallo silencioso — el dashboard cargará normalmente
      }
    }

    persistLayout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Countdown y redirect automático
  useEffect(() => {
    if (status !== "authenticated") return;

    const redirectTimeout = setTimeout(() => {
      router.push("/dashboard");
    }, 5000);

    const countdownInterval = setInterval(() => {
      setTimeToRedirect((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearTimeout(redirectTimeout);
      clearInterval(countdownInterval);
    };
  }, [status, router]);

  const handleRedirectNow = () => {
    router.push("/dashboard");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="absolute top-4 right-4 flex gap-2">
        <LanguageSwitcher />
        <Button variant="secondary" size="icon" onClick={handleThemeChange}>
          <Sun className="h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
          <Moon className="absolute h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
        </Button>
      </div>
      {status === "loading" || isLoading ? (
        <Loading />
      ) : status === "authenticated" ? (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="w-full max-w-md space-y-8 p-8 bg-card rounded-lg shadow-lg">
            <h1 className="flex justify-center text-2xl font-bold">
              {t("alreadyAuthenticated")}
            </h1>
            <p>
              {t("redirectingToDashboard")} {timeToRedirect} {t("seconds")} ...
            </p>
            <Button
              className="w-full"
              variant="destructive"
              onClick={handleRedirectNow}
            >
              {t("redirectNow")}
            </Button>
          </div>
        </div>
      ) : (
        <Login isLoading={isLoading} setIsLoading={setIsLoading} />
      )}
    </div>
  );
}
